import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { map, retry, switchMap, tap, catchError } from 'rxjs/operators';

import { SDK } from '@plugins/middleware-sdk';
import {
  ApplicationInsights,
  BlobStorageAccess,
  CodeIngressResponse,
  LoginDto,
  UserToken,
} from '@plugins/shared/types/auth';

// Allow that here as special manner to expose "load" method for MiddlewareSDKProxy to initialize the SDK on code ingress response from AuthService, which is the only place where it's used.
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MiddlewareSDKProxy } from '../../../../middleware-sdk/src/lib/middleware-sdk.proxy';

import { SERVERLESS } from './auth.const';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly httpClient = inject(HttpClient);
  private readonly serverless = inject<boolean>(SERVERLESS);
  private readonly sdk = inject<MiddlewareSDKProxy>(SDK);
  private pluginAuth: LoginDto | undefined;

  isLoggedIn(): boolean {
    return !!this.pluginAuth;
  }

  login(middlewareToken: string): Observable<UserToken> {
    if (this.serverless) {
      const user = jwtDecode<UserToken>(middlewareToken);
      this.pluginAuth = {
        user,
        auth: middlewareToken,
        middlewareAuth: middlewareToken,
      };
      return of(user);
    }
    return this.httpClient
      .post<LoginDto>('/api/auth/login', { jwt: middlewareToken })
      .pipe(
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status > 499) {
            return throwError(() => err);
          }
          return EMPTY;
        }),
        retry({ delay: 7000 }),
        tap((login) => {
          login.middlewareAuth = middlewareToken;
          this.pluginAuth = login;
        }),
        map(({ user }) => user),
      );
  }

  handover(token: string) {
    return this.httpClient
      .post<LoginDto>('/api/auth/refresh', { jwt: token })
      .pipe(
        tap((login) => {
          login.middlewareAuth = this.pluginAuth?.middlewareAuth ?? '';
          this.pluginAuth = login;
        }),
        map(({ user }) => user),
      );
  }

  exchangeCode(code: string, exchangeUrl: string): Observable<UserToken> {
    return this.httpClient
      .post<CodeIngressResponse>(exchangeUrl, { code })
      .pipe(
        switchMap(({ jwt, sdk }) => {
          void this.sdk.load({ jwt, sdk });
          return this.login(jwt);
        }),
      );
  }

  get authToken(): string {
    return this.pluginAuth?.auth ?? '';
  }

  get userToken(): UserToken | undefined {
    return this.pluginAuth?.user;
  }

  get instanceId(): string | undefined {
    return this.pluginAuth?.user.instance_id;
  }

  get blob(): BlobStorageAccess | undefined {
    return this.pluginAuth?.blob;
  }

  get appInsights(): ApplicationInsights | undefined {
    return this.pluginAuth?.appInsights;
  }
}
